import { Modal } from "@circleco/compass/components/Modal";
import { TextArea } from "@circleco/compass/components/TextArea";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import { ReportsList } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/CompassModal/ReportTab/ReportsList";
import { useModerationFooterActions } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/CompassModal/useModerationFooterActions";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  type FlaggedContentableType,
} from "@/react/types/FlaggedContent";

import { useApproveRejectContent } from "../useApproveRejectContent";
import type { ModerationActionOptions } from "../useModerationApi";
import { useVisibilityDescription } from "./useVisibilityDescription";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface ModerationInfoModalComponentProps {
  flaggedContentId: string;
  flaggedContentType: FlaggedContentableType;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const ModerationInfoModalComponent = ({
  flaggedContentId,
  flaggedContentType,
  open,
  onOpenChange,
}: ModerationInfoModalComponentProps) => {
  const {
    approveFlaggedContent,
    rejectFlaggedContent,
    flaggedContent,
    isApproving,
    isRejecting,
  } = useApproveRejectContent(flaggedContentId, flaggedContentType);

  const {
    author_is_flagged: isAuthorFlagged,
    flagged_contentable,
    initiator_is_flagged_for_connect: isFlaggedForConnect,
    reports = [],
    status,
  } = flaggedContent ?? {};
  const type = flagged_contentable?.type;
  const reportsCount = reports.length;
  const isEmbeddedChat =
    type === CHAT_ROOM_MESSAGE_TYPE &&
    Boolean(flagged_contentable?.is_embedded);

  const visibilityDescription = useVisibilityDescription(flaggedContent);

  const onApprove = useCallback(
    (options?: ModerationActionOptions) => {
      approveFlaggedContent({ options });
      onOpenChange(false);
    },
    [approveFlaggedContent, onOpenChange],
  );

  const onReject = useCallback(
    (reason: string | null, options?: ModerationActionOptions) => {
      rejectFlaggedContent({ reason, options });
      onOpenChange(false);
    },
    [rejectFlaggedContent, onOpenChange],
  );

  const {
    rejectState,
    setRejectState,
    handleRejectWithOptions,
    handleRejectWithReason,
    rejectMenuOptions,
    approveMenuOptions,
    rejectLabel,
    resetRejectState,
  } = useModerationFooterActions({
    flaggedContentId,
    isAuthorFlagged,
    isFlaggedForConnect,
    isEmbeddedChat,
    status: status ?? undefined,
    type,
    onApprove,
    onReject,
  });

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetRejectState();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetRejectState],
  );

  if (status === undefined || !type) {
    return null;
  }

  const isPending = !status;
  const shouldShowApprove = isPending || status === "rejected";
  const shouldShowReject = isPending || status === "approved";

  const rejectPrimaryAction = {
    label: t([i18nRoot, "reject"]),
    variant: "primary" as const,
    onClick: () => handleRejectWithOptions(),
    menuOptions: rejectMenuOptions.length > 0 ? rejectMenuOptions : undefined,
  };

  const approvePrimaryAction = {
    label: t([i18nRoot, "approve"]),
    disabled: isApproving,
    onClick: () => onApprove(),
    menuOptions: approveMenuOptions.length > 0 ? approveMenuOptions : undefined,
  };

  const secondaryAction =
    shouldShowApprove && shouldShowReject
      ? {
          label: t([i18nRoot, "approve"]),
          onClick: () => onApprove(),
        }
      : undefined;

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="lg">
      <Modal.Content>
        <Modal.Header
          title={t("flagged_contents.moderation_info.reports_title", {
            count: reportsCount,
          })}
          description={visibilityDescription}
        />
        <Modal.Body>
          <ReportsList reports={reports} />
          {rejectState.isActive && (
            <div className="pt-4">
              <TextArea
                rows={3}
                placeholder={t([i18nRoot, "rejection_note_placeholder"])}
                value={rejectState.reason}
                onChange={event =>
                  setRejectState(previous => ({
                    ...previous,
                    reason: event.target.value,
                  }))
                }
                label={t([i18nRoot, "moderation_note"])}
              />
            </div>
          )}
        </Modal.Body>
        {rejectState.isActive ? (
          <Modal.Footer
            primaryAction={{
              label: rejectLabel,
              variant: "primary",
              disabled: isRejecting,
              onClick: handleRejectWithReason,
            }}
          />
        ) : (
          <Modal.Footer
            secondaryAction={secondaryAction}
            primaryAction={
              shouldShowReject ? rejectPrimaryAction : approvePrimaryAction
            }
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
